import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Section";
import { useContent } from "@/i18n";
import { Shape3D, HandNote } from "@/components/Objects3D";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Beyond() {
  const { category } = useParams();
  const { ui, lang } = useContent();
  const [entries, setEntries] = useState([]);
  const [journalPosts, setJournalPosts] = useState([]);
  const meta = (ui.beyond.categories && ui.beyond.categories[category]) || { name: category, tagline: "" };
  const pick = (p, field) => (lang === "de" && p[`${field}_de`] ? p[`${field}_de`] : p[field]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/beyond/${category}`)
      .then((r) => r.json())
      .then(setEntries)
      .catch(() => {});
    if (category === "journaling") {
      fetch(`${API}/journal`)
        .then((r) => r.json())
        .then(setJournalPosts)
        .catch(() => {});
    } else {
      setJournalPosts([]);
    }
  }, [category]);

  return (
    <main className="min-h-screen bg-cream relative overflow-hidden" data-testid="beyond-page">
      <Shape3D shape="star" blue size={46} className="absolute right-[8%] top-24 opacity-80" />
      <div className="max-w-6xl mx-auto px-6 pt-10 pb-28 relative">
        <Link
          to="/"
          data-testid="beyond-back-link"
          className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-cocoa hover:text-choco transition-colors duration-300"
        >
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
          {ui.beyond.back}
        </Link>

        <Reveal>
          <p className="mt-14 text-xs tracking-[0.3em] uppercase font-bold text-cobalt">{ui.interests.chapter}</p>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tighter leading-none" data-testid="beyond-title">
            {meta.name}
          </h1>
          <HandNote className="mt-4 inline-block -rotate-2">{meta.tagline}</HandNote>
        </Reveal>

        {entries.length === 0 && journalPosts.length === 0 && (
          <Reveal delay={0.2}>
            <div className="mt-16 rounded-2xl border border-dashed border-line bg-ivory/60 p-14 text-center" data-testid="beyond-empty">
              <p className="font-serif italic text-3xl text-choco">{ui.beyond.emptyTitle}</p>
              <p className="mt-3 text-sm text-cocoa">{ui.beyond.emptyText}</p>
            </div>
          </Reveal>
        )}

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {entries.map((entry, i) => {            const rotations = ["-rotate-2", "rotate-1", "rotate-2", "-rotate-1", "rotate-1", "-rotate-2"];
            return (
              <Reveal key={entry.id} delay={0.05 * (i % 6)}>
                <article
                  className={`tilt-hover h-full bg-white border border-line rounded-xl p-3 shadow-[0_16px_36px_-24px_rgba(51,38,25,0.3)] ${rotations[i % rotations.length]}`}
                  data-testid={`beyond-entry-${i}`}
                >
                  {entry.image && (
                    <div className="overflow-hidden rounded-lg aspect-[4/3] bg-beige">
                      <img src={entry.image} alt={entry.title} className="frame-zoom w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="px-2 py-4">
                    <h2 className="font-serif text-xl tracking-tight leading-snug">{entry.title}</h2>
                    {entry.text && <p className="mt-2 text-sm leading-relaxed text-cocoa">{entry.text}</p>}
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] tracking-[0.2em] uppercase text-cocoa">
                      {entry.date && <span className="font-bold text-cobalt">{entry.date}</span>}
                      {entry.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={11} className="text-cobalt" /> {entry.location}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        {journalPosts.length > 0 && (
          <div className="mt-20" data-testid="beyond-journal-essays">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase font-bold text-cobalt">{ui.beyond.fromJournal}</p>
            </Reveal>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {journalPosts.map((post, i) => (
                <Reveal key={post.slug} delay={0.04 * (i % 6)}>
                  <Link
                    to={`/journal/${post.slug}`}
                    className="group flex flex-col h-full rounded-2xl border border-line bg-white overflow-hidden shadow-[0_20px_44px_-30px_rgba(51,38,25,0.3)] hover:-translate-y-1.5 transition-[transform,box-shadow] duration-500"
                    data-testid={`beyond-journal-${i}`}
                  >
                    {post.image && (
                      <div className="overflow-hidden aspect-[16/10]">
                        <img src={post.image} alt={pick(post, "title")} className="frame-zoom w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-cobalt">
                        {pick(post, "tag")} · {post.date}
                      </p>
                      <h2 className="mt-3 font-serif text-xl tracking-tight leading-snug group-hover:text-cobalt transition-colors duration-400">
                        {pick(post, "title")}
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-cocoa flex-1">{pick(post, "excerpt")}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold">
                        {ui.journal.read} <ArrowUpRight size={13} className="text-cobalt" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
